import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, TrendingUp, Calendar, ArrowRight, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatDateFr } from './payrollExportsHelpers';

const STATUS_VARIANTS = {
  DRAFT: 'secondary',
  CALCULATED: 'default',
  SUBMITTED: 'default',
  APPROVED: 'success',
  CLOSED: 'outline',
  LOCKED: 'outline',
  PAID: 'success',
};

const PayrollPeriodSummaryCards = ({ selectedPeriod, summary, formatCurrency }) => {
  const { t } = useTranslation();
  return (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <Card className="overflow-hidden">
      <CardHeader style={{ backgroundColor: "var(--lm-s2)", borderBottom: "1px solid var(--lm-border)" }}>
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(94,106,210,0.10)" }}>
            <Clock className="h-4 w-4" style={{ color: "#5e6ad2" }} />
          </div>
          <div>
            <span>{t('payrollUi.period.status')}</span>
            <p className="text-xs font-normal mt-0.5" style={{ color: "var(--lm-ink-3)" }}>{t('payrollUi.period.statusSub')}</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge
              variant={STATUS_VARIANTS[(selectedPeriod.status || '').toUpperCase()] || 'outline'}
              className="text-sm px-3 py-1"
            >
              {t(`payrollUi.period.${(selectedPeriod.status || '').toUpperCase()}`, { defaultValue: selectedPeriod.status })}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground space-y-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{t('payrollUi.period.fromDate', { date: formatDateFr(selectedPeriod.start_date) })}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span>{t('payrollUi.period.toDate', { date: formatDateFr(selectedPeriod.end_date) })}</span>
            </div>
            {selectedPeriod.processed_at && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Activity className="h-3 w-3" />
                <span>{t('payrollUi.period.lastCalc')} {new Date(selectedPeriod.processed_at).toLocaleString('fr-FR')}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="overflow-hidden">
      <CardHeader style={{ backgroundColor: "var(--lm-s2)", borderBottom: "1px solid var(--lm-border)" }}>
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(39,166,68,0.10)" }}>
            <TrendingUp className="h-4 w-4" style={{ color: "#27a644" }} />
          </div>
          <div>
            <span>{t('payrollUi.period.financialSummary')}</span>
            <p className="text-xs font-normal mt-0.5" style={{ color: "var(--lm-ink-3)" }}>{t('payrollUi.period.financialSub')}</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="space-y-1">
              <p className="text-2xl font-bold text-foreground">{formatCurrency(summary?.total_gross)}</p>
              <p className="text-xs text-muted-foreground">{t('payrollUi.period.grossTotal')}</p>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-green-600">{formatCurrency(summary?.total_net)}</p>
              <p className="text-xs text-muted-foreground">{t('payrollUi.period.netPay')}</p>
            </div>
          </div>
          <div className="text-center pt-2 border-t">
            <p className="text-lg font-semibold text-muted-foreground">{summary?.total_employees || 0}</p>
            <p className="text-xs text-muted-foreground">{t('payrollUi.period.employeesProcessed')}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
  );
};

export default PayrollPeriodSummaryCards;
